const PaymentMethodType = {
  APPLE_PAY: 'apple-pay',
  GOOGLE_PAY: 'google-pay',
  BANK: 'bank',
  // Everything that settles through HitPay's hosted checkout — cards and the
  // wallets that ride on them. The surface is the same for all of them.
  CARD: 'card',
  PAYNOW: 'paynow',
};

const PAYMENT_METHODS = [
  {
    type: PaymentMethodType.APPLE_PAY,
    label: 'Apple Pay',
    icon: 'apple-pay',
    available: true,
  },
  {
    type: PaymentMethodType.GOOGLE_PAY,
    label: 'Google Pay',
    icon: 'google-pay',
    available: true,
  },
];

/**
 * What the member is shown for the hosted-checkout rail, in one place.
 *
 * There were four names for this one thing: the tile said "Card or PayNow", its
 * subtitle said "Pay securely via HitPay" in one view and "Tap to pay securely
 * via HitPay" in the other, and the label written onto the ledger — the one the
 * receipt reads back — said "Card / Wallet". A member picked one name and was
 * shown another.
 *
 * The processor name stays deliberately: it is a trust signal at the moment of
 * paying. It belongs in the subtitle, not the label, so it never becomes the
 * name of the thing.
 */
const CARD_RAIL_LABEL = 'Card or PayNow';
const CARD_RAIL_SUBTITLE = 'Pay securely via HitPay';

/**
 * Generic labels a stored ledger entry may carry from before the rail had one
 * name. Display code maps these forward; anything else the ledger captured
 * (e.g. "Visa •••• 4242") is more specific than CARD_RAIL_LABEL and is kept.
 */
const LEGACY_CARD_RAIL_LABELS = ['Card / Wallet', 'Card'];

/**
 * Why a payment is still settling, per rail, and what to do about it.
 *
 * There were three wordings for one state — "being confirmed" / "being
 * processed" / "still confirming" — and the receipt applied the bank rail's
 * "up to 1 working day" to card payments too, where a pending status is just
 * webhook lag measured in seconds. The day-long window is the eGIRO/FAST one
 * and matches the other giving screens that quote it.
 *
 * Neither promises a notification any more. Both used to end "you'll be
 * notified once it completes" and nothing sends that — so the member sat
 * waiting for a message that was never coming, and the one thing they actually
 * needed to know went unsaid: hold, and do not pay a second time. "Shows as
 * successful" is deliberate — that is the word the My Payments chip and the
 * receipt both use for the cleared state.
 */
const PENDING_MESSAGE = {
  card: "We're still confirming your payment. Please don't pay again until it shows as successful.",
  bank: "Your transfer is being processed. This can take up to 1 working day. Please don't pay again until it shows as successful.",
};

function pendingMessage(methodType) {
  return methodType === 'bank' ? PENDING_MESSAGE.bank : PENDING_MESSAGE.card;
}

/**
 * Card-rail failure. "You have not been charged" is safe to state here and is
 * the first thing a member wants to know:
 *
 *   - a ledger FAILED / CANCELLED / EXPIRED row was never captured;
 *   - both throw sites in payWithHitPay fire before the browser opens
 *     (openHitPayCheckout rethrows only when Browser.open itself fails);
 *   - and a genuine "we cannot tell" resolves to PENDING, never to failure.
 *
 * Do NOT reuse this on the bank rail. A FAST transfer settles through the
 * giving service and nothing here has established that a failure there cannot
 * debit the member.
 */
const CARD_FAILURE_MESSAGE =
  "The payment didn't go through and you have not been charged. You can start the purchase again from Next Steps.";

/**
 * The hosted checkout ended without a completed payment — a ledger EXPIRED or
 * CANCELLED row. Distinct from CARD_FAILURE_MESSAGE on purpose: nothing was
 * declined, so "the payment didn't go through" describes an attempt the member
 * never made and reads as a fault in the app.
 *
 * This used to share the decline copy, which is how members whose live checkout
 * the reconcile sweep had expired under them (see PAYMENT_ABANDON_MINUTES in
 * hogc-one-api) were told a payment had failed.
 */
const CHECKOUT_ABANDONED_MESSAGE =
  'The checkout closed before the payment was completed, so you have not been charged. You can start the purchase again from Next Steps.';

/**
 * Failure with no claim either way about the money. Used on the bank rail and
 * for anything whose outcome this client cannot vouch for.
 */
const GENERIC_FAILURE_MESSAGE =
  "The payment didn't go through. You can start the purchase again from Next Steps.";

const PaymentCategory = {
  TITHE: 'tithe',
  OFFERING: 'offering',
  DONATION: 'donation',
  EVENT_REGISTRATION: 'event-registration',
  BUILDING_FUND: 'building-fund',
};

const PaymentStatus = {
  SUCCESS: 'success',
  PENDING: 'pending',
  CANCELLED: 'cancelled',
  FAILED: 'failed',
  REFUNDED: 'refunded',
};

const PIN_REQUIRED_THRESHOLD = 1000;
const DEFAULT_TRANSACTION_LIMIT = 10000;

/**
 * Feature flag for the payment module.
 *
 * Every surface that can reach checkout must read this one name. It gated the
 * /my-payments and /payment-details routes only, which left the Next Steps "buy
 * the book" CTA opening the sheet regardless — a live money path, reachable the
 * moment the unrelated `courses` flag went on. Shared constant so a second
 * entry point cannot quietly ship ungated.
 */
const MOBILE_PAYMENTS_FLAG = 'mobile-payments';

module.exports = {
  PaymentMethodType,
  PAYMENT_METHODS,
  CARD_RAIL_LABEL,
  CARD_RAIL_SUBTITLE,
  LEGACY_CARD_RAIL_LABELS,
  PENDING_MESSAGE,
  pendingMessage,
  CARD_FAILURE_MESSAGE,
  CHECKOUT_ABANDONED_MESSAGE,
  GENERIC_FAILURE_MESSAGE,
  PaymentCategory,
  PaymentStatus,
  PIN_REQUIRED_THRESHOLD,
  DEFAULT_TRANSACTION_LIMIT,
  MOBILE_PAYMENTS_FLAG,
};
